// Story #2777 Stream A3 (#2745): adds 'own-all' (all my projects) and
// 'ecosystem' (docs in disclosure-linked projects). The backend gates
// every ecosystem result with check_permission(user, doc, 'read') so
// the UI only needs to surface the option — privacy is enforced server-side.

type Scope = 'own' | 'shared' | 'journals' | 'patents' | 'all' | 'own-all' | 'ecosystem'

interface ScopeToggleProps {
  scope: Scope
  onChange: (scope: Scope) => void
  /** live counts derived from the most recent search response — a scope only
   * gets a badge once we actually have real data for it (e.g. 'all' always
   * has data.total; 'own'/'shared'/'journals' get sub-counts when scope==='all'
   * mixes results from all three; other scopes pick up a badge once visited). */
  counts?: Partial<Record<Scope, number>>
}

const SCOPES: { value: Scope; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'own', label: 'Own' },
  { value: 'own-all', label: 'All my projects' },
  { value: 'ecosystem', label: 'Ecosystem (disclosure-linked)' },
  { value: 'shared', label: 'Shared' },
  { value: 'journals', label: 'Journals' },
  { value: 'patents', label: 'Patents' },
]

export default function ScopeToggle({ scope, onChange, counts }: ScopeToggleProps) {
  return (
    <div className="flex gap-1 bg-gray-100 rounded-lg p-1 flex-wrap">
      {SCOPES.map((s) => {
        const count = counts?.[s.value]
        return (
          <button
            key={s.value}
            onClick={() => onChange(s.value)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${
              scope === s.value
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {s.label}
            {count != null && (
              <span
                data-testid={`scope-count-${s.value}`}
                className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                  scope === s.value ? 'bg-iflow-100 text-iflow-700' : 'bg-gray-200 text-gray-600'
                }`}
              >
                {count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

export type { Scope }
